import React, { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { X, MapPin, ExternalLink, Volume2, Plus } from 'lucide-react';
import { useNavigationState } from '@/hooks/useNavigationState';
import PlaceAudioPlayer from '@/components/PlaceAudioPlayer';

interface PlaceDetailPopupProps {
  placeName: string;
  latitude: number;
  longitude: number;
  onClose: () => void;
  // Optional state to save before navigation
  currentPageState?: any;
  // Optional callback to open podcast generator
  onOpenPodcastGenerator?: (locationDetails: any) => void;
}

interface PlaceData {
  Name: string;
  Description_FR?: string;
  Description_EN?: string;
  Image?: string;
  Adresse?: string;
  City?: string;
  Country?: string;
  AudioFile_EN?: string;
  AudioFile_FR?: string;
}

const PlaceDetailPopup: React.FC<PlaceDetailPopupProps> = ({
  placeName,
  latitude,
  longitude,
  onClose,
  currentPageState,
  onOpenPodcastGenerator
}) => {
  const { navigateToPlace } = useNavigationState();
  const [placeData, setPlaceData] = useState<PlaceData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPlaceData = async () => {
      try {
        setLoading(true);
        
        // First try to find by exact name
        let { data, error } = await supabase
          .from('podcasts')
          .select('Name, Description_FR, Description_EN, Image, Adresse, City, Country, AudioFile_EN, AudioFile_FR')
          .eq('Name', placeName)
          .single();

        // If not found, try to find by location proximity (within ~100m)
        if (error || !data) {
          const { data: nearbyData, error: nearbyError } = await supabase
            .from('podcasts')
            .select('Name, Description_FR, Description_EN, Image, Adresse, City, Country, AudioFile_EN, AudioFile_FR, Latitude, Longitude')
            .not('Latitude', 'is', null)
            .not('Longitude', 'is', null);

          if (!nearbyError && nearbyData) {
            // Find the closest place within reasonable distance
            const closestPlace = nearbyData.find(place => {
              const distance = Math.sqrt(
                Math.pow(Number(place.Latitude) - latitude, 2) + 
                Math.pow(Number(place.Longitude) - longitude, 2)
              );
              return distance < 0.001; // Approximately 100m
            });

            if (closestPlace) {
              data = closestPlace;
            }
          }
        }

        setPlaceData(data);
      } catch (error) {
        console.error('Error fetching place data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchPlaceData();
  }, [placeName, latitude, longitude]);

  const handleViewPodcast = () => {
    if (placeData) {
      // Use navigateToPlace to save current state before navigation
      navigateToPlace(placeData.Name, currentPageState);
    }
  };

  const openGoogleMaps = () => {
    const url = `https://maps.google.com/maps?q=${latitude},${longitude}`;
    window.open(url, '_blank');
  };

  const handleCreatePodcast = () => {
    if (onOpenPodcastGenerator) {
      const locationDetails = {
        name: placeName,
        city: placeData?.City || 'Unknown',
        country: placeData?.Country || 'Unknown', 
        continent: 'Unknown',
        latitude,
        longitude
      };
      onOpenPodcastGenerator(locationDetails);
      onClose();
    }
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
        <Card className="w-full max-w-md bg-background/95 backdrop-blur-md rounded-2xl border border-border/50 shadow-xl">
          <CardContent className="p-6">
            <div className="flex items-center justify-center">
              <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full"></div>
            </div>
            <p className="text-center mt-4">Chargement des informations...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div 
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      <Card className="w-full max-w-md max-h-[80vh] overflow-y-auto bg-background/95 backdrop-blur-md rounded-2xl border border-border/50 shadow-xl">
        <CardHeader className="flex flex-row items-start justify-between space-y-0 pb-3">
          <CardTitle className="text-lg font-semibold pr-4">
            {placeData?.Name || placeName}
          </CardTitle>
          <Button
            variant="ghost"
            size="sm"
            onClick={onClose}
            className="h-8 w-8 p-0"
          >
            <X className="h-4 w-4" />
          </Button>
        </CardHeader>
        
        <CardContent className="space-y-4 pt-0">
          {placeData?.Image && (
            <div className="aspect-video w-full overflow-hidden rounded-lg">
              <img
                src={placeData.Image}
                alt={placeData.Name}
                className="w-full h-full object-cover"
                onError={(e) => {
                  const target = e.target as HTMLImageElement;
                  target.style.display = 'none';
                }}
              />
            </div>
          )}

          {/* City and Country under image */}
          {placeData?.City && placeData?.Country && (
            <div className="text-sm text-muted-foreground">
              <div className="flex items-center gap-2">
                <MapPin className="h-4 w-4 flex-shrink-0" />
                <span>{placeData.City}, {placeData.Country}</span>
              </div>
            </div>
          )}

          {/* Language Flags and Audio Section */}
          {placeData && (
            <div className="space-y-3">
              {/* Language flags row */}
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground">Langues disponibles:</span>
                <div className="flex items-center gap-2">
                  {/* French flag */}
                  {placeData.AudioFile_FR && (
                    <div className="w-6 h-6 rounded-full overflow-hidden border border-border/50">
                      <img 
                        src="/src/assets/flag-france.png" 
                        alt="Français"
                        className="w-full h-full object-cover"
                      />
                    </div>
                  )}
                  
                  {/* English flag */}
                  {placeData.AudioFile_EN && (
                    <div className="w-6 h-6 rounded-full overflow-hidden border border-border/50">
                      <img 
                        src="/src/assets/flag-uk.png" 
                        alt="English"
                        className="w-full h-full object-cover"
                      />
                    </div>
                  )}
                  
                  {/* Add button - only show if both languages are not available */}
                  {onOpenPodcastGenerator && !(placeData.AudioFile_FR && placeData.AudioFile_EN) && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleCreatePodcast}
                      className="w-6 h-6 rounded-full p-0 border border-dashed border-muted-foreground/50 hover:border-primary"
                      title="Créer un podcast"
                    >
                      <Plus className="h-3 w-3 text-muted-foreground" />
                    </Button>
                  )}
                </div>
              </div>

              {/* Audio Player */}
              {(placeData.AudioFile_EN || placeData.AudioFile_FR) && (
                <PlaceAudioPlayer 
                  placeName={placeName}
                  latitude={latitude}
                  longitude={longitude}
                />
              )}
            </div>
          )}

          {(placeData?.Description_FR || placeData?.Description_EN) && (
            <div className="text-sm">
              <p className="text-muted-foreground">
                {placeData.Description_FR || placeData.Description_EN}
              </p>
            </div>
          )}

          {/* Action Buttons - Compact for mobile */}
          <div className="flex gap-2">
            {/* Google Maps Button - Icon only */}
            <Button 
              variant="outline" 
              onClick={openGoogleMaps}
              className="p-2 h-9 w-9"
              title="Ouvrir sur Maps"
            >
              <MapPin className="h-4 w-4" />
            </Button>

            {/* Podcast Button */}
            {placeData ? (
                <Button 
                  onClick={handleViewPodcast} 
                  className="flex-1 h-9 text-sm"
                >
                  <Volume2 className="h-4 w-4 mr-2" />
                  En découvrir d'avantage
                </Button>
            ) : (
              onOpenPodcastGenerator && (
                <Button 
                  variant="outline"
                  onClick={handleCreatePodcast}
                  className="flex-1 h-9 text-sm"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Créer un podcast
                </Button>
              )
            )}
          </div>

          {!placeData && !onOpenPodcastGenerator && (
            <div className="text-center py-4">
              <p className="text-muted-foreground text-sm">
                Aucune information disponible pour ce lieu
              </p>
              <p className="text-xs text-muted-foreground mt-2">
                Coordonnées: {latitude.toFixed(6)}, {longitude.toFixed(6)}
              </p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default PlaceDetailPopup;